import {
  DeviceDetail,
  DeviceRestrictionScope,
  DeviceSummary,
  DeviceStatus,
  httpFetchDevice,
  httpUpdateDeviceStatus,
} from "api/admin/device";
import { DateTime } from "luxon";
import { useEffect, useId, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

const scopes: DeviceRestrictionScope[] = [
  "signup",
  "login",
  "rewards",
  "outbound_transactions",
];

export const deviceDateLabel = (value?: string | null) =>
  value ? DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_MED) : "—";

export const humanizeDeviceValue = (value: string) => value.replace(/_/g, " ");

export const deviceOsLabel = (
  device: Pick<DeviceSummary, "platform" | "metadata">,
) =>
  [
    device.metadata.systemName ||
      (device.platform === "ios" ? "iOS" : "Android"),
    device.metadata.systemVersion,
  ]
    .filter(Boolean)
    .join(" ");

export const integrityHelp =
  "Whether Apple App Attest or Google Play Integrity could verify that the request came from the genuine Bitoshi app. Verified means it passed; unavailable means the check could not run; rejected means the evidence failed verification.";
export const recallHelp =
  "A durable risk marker maintained through Apple or Google that helps Bitoshi recognize a device previously placed under review, restriction, or block, including after an app reinstall.";
export const attestationHelp =
  "A cryptographic check from Apple or Google that helps prove the request came from the genuine Bitoshi app on the reported device. Unavailable means the provider could not complete the check; rejected means the evidence failed verification.";
const trustedHelp =
  "Active account trust allows this device to sign in after the normal credentials. The first trust date is preserved while expiry can extend with use. Removing the device revokes trust and its sessions. Platform integrity is a separate check.";
export const deviceStatusHelp =
  "The admin-controlled state of a device. Active allows normal use, review keeps it visible for investigation, restricted blocks selected capabilities, and blocked prevents protected use.";
export const loginCoverageHelp =
  "The percentage of login attempts in the last seven days that could be linked to a known device.";
export const transactionCoverageHelp =
  "The percentage of transactions in the last seven days that recorded which known device performed them.";
export const recallSyncHelp =
  "Devices whose latest review, restriction, or block still needs to be written to Apple DeviceCheck or Google Device Recall at the next verified check-in.";
export const providerUnavailableHelp =
  "Integrity checks that could not complete in the last seven days because Apple or Google was unavailable, throttled, or not supported on the device. This is different from rejected evidence.";
export const associatedAccountsHelp =
  "The number of Bitoshi accounts that have authenticated on this device and remain linked to it.";

type TooltipAlignment = "left" | "center" | "right";

export const HelpTooltip = ({
  label,
  children,
  align = "center",
}: {
  label: string;
  children: string;
  align?: TooltipAlignment;
}) => {
  const tooltipId = useId();
  const position =
    align === "left"
      ? "left-0"
      : align === "right"
        ? "left-1/2 -translate-x-1/2 md:left-auto md:right-0 md:translate-x-0"
        : "left-1/2 -translate-x-1/2";

  return (
    <span className="relative inline-flex align-middle shrink-0 group">
      <button
        type="button"
        aria-label={`About ${label}`}
        aria-describedby={tooltipId}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-gray-400 text-[10px] font-semibold leading-none text-gray-500 transition hover:border-gray-700 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1"
      >
        <span aria-hidden="true">?</span>
      </button>
      <span
        id={tooltipId}
        role="tooltip"
        className={`pointer-events-none invisible absolute top-full z-50 mt-2 w-48 rounded-lg bg-gray-900 px-3 py-2 text-left text-xs font-normal normal-case leading-relaxed tracking-normal text-white opacity-0 shadow-xl transition-opacity group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 md:w-64 ${position}`}
      >
        {children}
      </span>
    </span>
  );
};

export const DeviceBadge = ({ value }: { value: string }) => {
  const color =
    value === "verified" || value === "active" || value === "clear"
      ? "bg-green-100 text-green-800"
      : value === "blocked" || value === "rejected"
        ? "bg-red-100 text-red-800"
        : value === "restricted" || value === "flagged"
          ? "bg-orange-100 text-orange-800"
          : "bg-gray-100 text-gray-700";

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${color}`}
    >
      {humanizeDeviceValue(value)}
    </span>
  );
};

const StatusEditor = ({ detail }: { detail: DeviceDetail }) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<DeviceStatus>(detail.device.status);
  const [reason, setReason] = useState(detail.device.statusReason || "");
  const [selectedScopes, setSelectedScopes] = useState<
    DeviceRestrictionScope[]
  >(detail.device.restrictionScopes || []);
  const [expiresAt, setExpiresAt] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    setStatus(detail.device.status);
    setReason(detail.device.statusReason || "");
    setSelectedScopes(detail.device.restrictionScopes || []);
    setExpiresAt(
      detail.device.statusExpiresAt
        ? DateTime.fromISO(detail.device.statusExpiresAt).toFormat(
            "yyyy-LL-dd'T'HH:mm",
          )
        : "",
    );
    setMessage("");
  }, [detail.device]);

  const mutation = useMutation(
    () =>
      httpUpdateDeviceStatus(detail.device.publicId, {
        status,
        reason: reason.trim(),
        restrictionScopes: status === "restricted" ? selectedScopes : [],
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      }),
    {
      onSuccess: async () => {
        setMessage(
          "Status updated. Platform recall will sync on the next verified check-in.",
        );
        await Promise.all([
          queryClient.invalidateQueries(["device", detail.device.publicId]),
          queryClient.invalidateQueries("devices"),
          queryClient.invalidateQueries("deviceMetrics"),
        ]);
      },
      onError: (error: any) =>
        setMessage(error?.message || "Status update failed"),
    },
  );

  const toggleScope = (scope: DeviceRestrictionScope) =>
    setSelectedScopes((current) =>
      current.includes(scope)
        ? current.filter((item) => item !== scope)
        : [...current, scope],
    );

  const invalid =
    reason.trim().length < 3 ||
    (status === "restricted" && selectedScopes.length === 0);

  return (
    <section className="p-4 border border-gray-200 rounded-xl bg-gray-50">
      <h4 className="font-semibold text-gray-900">Change device status</h4>
      <div className="grid gap-3 mt-3 md:grid-cols-2">
        <label className="text-sm text-gray-600">
          Status
          <select
            className="w-full mt-1 text-sm border-gray-300 rounded-lg"
            value={status}
            onChange={(event) => setStatus(event.target.value as DeviceStatus)}
          >
            {(
              ["active", "review", "restricted", "blocked"] as DeviceStatus[]
            ).map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-600">
          Expires (optional)
          <input
            className="w-full mt-1 text-sm border-gray-300 rounded-lg"
            type="datetime-local"
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
          />
        </label>
      </div>
      {status === "restricted" ? (
        <div className="mt-3">
          <p className="text-sm text-gray-600">Restricted capabilities</p>
          <div className="flex flex-wrap gap-3 mt-2">
            {scopes.map((scope) => (
              <label key={scope} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selectedScopes.includes(scope)}
                  onChange={() => toggleScope(scope)}
                />
                {humanizeDeviceValue(scope)}
              </label>
            ))}
          </div>
        </div>
      ) : null}
      <label className="block mt-3 text-sm text-gray-600">
        Reason (required and audit logged)
        <textarea
          className="w-full mt-1 text-sm border-gray-300 rounded-lg"
          rows={2}
          maxLength={160}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
      <button
        type="button"
        disabled={invalid || mutation.isLoading}
        onClick={() => mutation.mutate()}
        className="px-4 py-2 mt-3 text-sm font-medium text-white bg-black rounded-lg disabled:cursor-not-allowed disabled:opacity-40"
      >
        {mutation.isLoading ? "Saving…" : "Save status"}
      </button>
      {message ? <p className="mt-2 text-sm text-gray-600">{message}</p> : null}
    </section>
  );
};

const DevicePanel = ({
  publicId,
  onClose,
}: {
  publicId: string;
  onClose: () => void;
}) => {
  const { data, isLoading, isError } = useQuery<DeviceDetail>(
    ["device", publicId],
    () => httpFetchDevice(publicId),
  );

  return (
    <aside
      role="dialog"
      aria-modal="true"
      aria-label="Device details"
      className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto border-l border-gray-200 bg-white p-6 shadow-2xl md:w-[680px]"
    >
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading device…</p>
      ) : isError || !data ? (
        <div className="flex items-start justify-between gap-4">
          <p className="text-sm text-red-600">
            Device details could not be loaded.
          </p>
          <button type="button" onClick={onClose} className="text-sm underline">
            Close
          </button>
        </div>
      ) : (
        <DevicePanelContent detail={data} onClose={onClose} />
      )}
    </aside>
  );
};

const DevicePanelContent = ({
  detail,
  onClose,
}: {
  detail: DeviceDetail;
  onClose: () => void;
}) => {
  const { device } = detail;

  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs tracking-wide text-gray-500 uppercase">
            Device
          </p>
          <h3 className="mt-1 text-xl font-semibold">
            {device.metadata.model || "Unknown model"}
          </h3>
          <p className="mt-1 font-mono text-xs text-gray-500 break-all">
            {device.publicId}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border px-3 py-1.5 text-sm"
        >
          Close
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-5 text-sm text-center md:grid-cols-3">
        <div>
          <p className="text-gray-500">Status</p>
          <div className="mt-1">
            <DeviceBadge value={device.status} />
          </div>
        </div>
        <div>
          <p className="flex items-center justify-center gap-1.5 text-gray-500">
            Integrity
            <HelpTooltip label="device integrity">{integrityHelp}</HelpTooltip>
          </p>
          <div className="mt-1">
            <DeviceBadge value={device.integrityStatus} />
          </div>
        </div>
        <div>
          <p className="flex items-center justify-center gap-1.5 text-gray-500">
            Recall
            <HelpTooltip label="device recall" align="right">
              {recallHelp}
            </HelpTooltip>
          </p>
          <div className="mt-1">
            <DeviceBadge value={device.platformRecallStatus} />
          </div>
        </div>
        <div>
          <p className="text-gray-500">Platform</p>
          <p className="mt-1 font-medium">{device.platform}</p>
        </div>
        <div>
          <p className="text-gray-500">App / build</p>
          <p className="mt-1 font-medium">
            {device.metadata.appVersion || "—"} /{" "}
            {device.metadata.buildNumber || "—"}
          </p>
        </div>
        <div>
          <p className="text-gray-500">OS</p>
          <p className="mt-1 font-medium">{deviceOsLabel(device)}</p>
        </div>
        <div>
          <p className="text-gray-500">First seen</p>
          <p className="mt-1 font-medium">
            {deviceDateLabel(device.firstSeenAt)}
          </p>
        </div>
        <div>
          <p className="text-gray-500">Last seen</p>
          <p className="mt-1 font-medium">
            {deviceDateLabel(device.lastSeenAt)}
          </p>
        </div>
        <div>
          <p className="flex items-center justify-center gap-1.5 text-gray-500">
            Last integrity check
            <HelpTooltip label="last integrity check" align="right">
              The latest time this device sent integrity evidence. Check the
              Integrity status and reason to see whether that evidence passed.
            </HelpTooltip>
          </p>
          <p className="mt-1 font-medium">
            {deviceDateLabel(device.lastAttestedAt)}
          </p>
        </div>
      </div>

      {device.integrityReason ? (
        <p className="p-3 mt-4 text-sm text-red-800 rounded-lg bg-red-50">
          {device.integrityReason}
        </p>
      ) : null}
      {device.recallSyncPending ? (
        <p className="p-3 mt-4 text-sm text-orange-800 rounded-lg bg-orange-50">
          Recall status is waiting for the next verified device check-in.
        </p>
      ) : null}

      <div className="mt-6">
        <StatusEditor detail={detail} />
      </div>

      <section className="mt-7">
        <h4 className="font-semibold">
          Associated Bitoshi accounts ({detail.associatedAccounts.length})
        </h4>
        <div className="mt-2 border divide-y rounded-xl">
          {detail.associatedAccounts.length ? (
            detail.associatedAccounts.map((account) => (
              <div key={account.id} className="p-3 text-sm">
                <p className="font-medium">
                  @{account.user?.userName || `user-${account.userId}`}
                </p>
                <p className="text-gray-500">
                  {account.user?.email ||
                    account.user?.phoneNumber ||
                    "No contact shown"}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1.5">
                    {!account.revokedAt &&
                    account.trustExpiresAt &&
                    new Date(account.trustExpiresAt).getTime() > Date.now()
                      ? "Trusted account"
                      : "Untrusted account"}
                    <HelpTooltip
                      label="trusted account association"
                      align="left"
                    >
                      {trustedHelp}
                    </HelpTooltip>
                  </span>
                  {" · "}
                  First trusted: {deviceDateLabel(account.firstTrustedAt)} ·
                  Trust expires: {deviceDateLabel(account.trustExpiresAt)} ·
                  Last seen: {deviceDateLabel(account.lastSeenAt)}
                </p>
              </div>
            ))
          ) : (
            <p className="p-3 text-sm text-gray-500">
              No account has been linked.
            </p>
          )}
        </div>
      </section>

      <section className="mt-7">
        <h4 className="font-semibold">Recent login attempts</h4>
        <div className="mt-2 overflow-x-auto border rounded-xl">
          <table className="min-w-full text-xs text-left">
            <thead className="text-gray-500 bg-gray-50">
              <tr>
                <th className="p-2">Result</th>
                <th className="p-2">Account</th>
                <th className="p-2">
                  <span className="inline-flex items-center gap-1.5">
                    Integrity
                    <HelpTooltip label="login device integrity">
                      {integrityHelp}
                    </HelpTooltip>
                  </span>
                </th>
                <th className="p-2">Time</th>
              </tr>
            </thead>
            <tbody>
              {detail.loginAttempts.slice(0, 12).map((attempt) => (
                <tr key={attempt.id} className="border-t">
                  <td className="p-2">
                    <DeviceBadge
                      value={attempt.success ? "verified" : "rejected"}
                    />
                  </td>
                  <td className="p-2">{attempt.username}</td>
                  <td className="p-2">{attempt.integrity_status || "—"}</td>
                  <td className="p-2 whitespace-nowrap">
                    {deviceDateLabel(attempt.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-7">
        <h4 className="font-semibold">Recent attributed transactions</h4>
        <div className="mt-2 border divide-y rounded-xl">
          {detail.transactions.slice(0, 12).map((transaction) => (
            <div
              key={transaction.id}
              className="flex items-center justify-between gap-3 p-3 text-sm"
            >
              <div>
                <p className="font-medium">
                  {humanizeDeviceValue(transaction.transactionType)} ·{" "}
                  {transaction.amount}{" "}
                  {transaction.currencyType === "crypto" &&
                  !["bill", "withdrawal", "refund"].includes(
                    transaction.transactionType,
                  )
                    ? transaction.crypto
                    : transaction.currency || "Fiat"}
                </p>
                <p className="text-xs text-gray-500">
                  {deviceDateLabel(transaction.createdAt)}
                  {transaction.riskFlagReason
                    ? ` · ${transaction.riskFlagReason}`
                    : ""}
                </p>
              </div>
              <DeviceBadge value={transaction.status} />
            </div>
          ))}
          {!detail.transactions.length ? (
            <p className="p-3 text-sm text-gray-500">
              No transactions have been attributed to this device.
            </p>
          ) : null}
        </div>
      </section>

      <section className="pb-10 mt-7">
        <h4 className="font-semibold">Device audit trail</h4>
        <div className="mt-2 border divide-y rounded-xl">
          {detail.events.slice(0, 20).map((event) => (
            <div key={event.id} className="p-3 text-sm">
              <div className="flex justify-between gap-3">
                <div className="flex items-center gap-1.5 font-medium">
                  {humanizeDeviceValue(event.type)}
                  {event.type.startsWith("attestation_") ? (
                    <HelpTooltip label="attestation event" align="left">
                      {attestationHelp}
                    </HelpTooltip>
                  ) : null}
                </div>
                <p className="text-xs text-gray-500">
                  {deviceDateLabel(event.createdAt)}
                </p>
              </div>
              <p className="mt-1 text-xs text-gray-500 break-words">
                {JSON.stringify(event.metadata)}
              </p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
};

export const DeviceDetailsOverlay = ({
  publicId,
  onClose,
}: {
  publicId: string;
  onClose: () => void;
}) => (
  <>
    <button
      type="button"
      aria-label="Close device details"
      className="fixed inset-0 z-40 w-full h-full cursor-default bg-black/30"
      onClick={onClose}
    />
    <DevicePanel publicId={publicId} onClose={onClose} />
  </>
);
